import React, { Suspense, useEffect } from "react";
import {
  Routes,
  Route,
  Navigate,
  useLocation,
  useNavigate,
} from "react-router-dom";
import NavMenu from "./components/NavMenu";
import { RoleGuard, useAuth } from "./contexts/AuthContext";
import LoginPage from "./pages/LoginPage";
import { MedewerkerType as Role } from "./enums";
import NotificationTray from "./components/ui/NotificationTray";
import DossierQuickSearch from "./components/ui/DossierQuickSearch";
import GlobalShortcuts from "./components/ui/GlobalShortcuts";
import { UploadProgressFab } from "./components/dossier/UploadProgressFab";
import PaginaLader from "./components/ui/PaginaLader";
import { bewaarDoel, neemDoel } from "./utils/naLoginDoel";
import { isDevOmgeving } from "./utils/omgeving";

const DashboardPage = React.lazy(() => import("./pages/DashboardPage"));
const MeldingLinkPagina = React.lazy(() => import("./pages/MeldingLinkPagina"));
const MijnInstellingenPagina = React.lazy(
  () => import("./pages/MijnInstellingenPagina"),
);
const AdminPage = React.lazy(() => import("./pages/AdminPage"));
const DossierCreatePage = React.lazy(() =>
  import("./pages/DossierCreatePage").then((module) => ({
    default: module.DossierCreatePage,
  })),
);
// De pdf-editor in een eigen venster (26-10-2026); laadt EmbedPDF pas daar.
const PdfBewerkenPagina = React.lazy(() => import("./pages/PdfBewerkenPagina"));
const DossierPagina = React.lazy(() =>
  import("./pages/DossierPagina").then((module) => ({
    default: module.DossierPagina,
  })),
);
const MedewerkerPage = React.lazy(() => import("./pages/MedewerkerPage"));
const ProductPage = React.lazy(() => import("./pages/ProductPage"));
const FinancienPage = React.lazy(() => import("./pages/FinancienPage"));
const WaarnemersPage = React.lazy(() => import("./pages/WaarnemersPage"));
const FacturenPage = React.lazy(() => import("./pages/FacturenPage"));
const AanvullendeFacturenPage = React.lazy(
  () => import("./pages/AanvullendeFacturenPage"),
);
const PlanPage = React.lazy(() =>
  import("./pages/PlanPage").then((module) => ({
    default: module.PlanPage,
  })),
);
const VoorbereidingenPagina = React.lazy(
  () => import("./pages/VoorbereidingenPagina"),
);
const PayrollPage = React.lazy(() => import("./pages/PayrollPage"));
const KennisbankPage = React.lazy(() => import("./pages/KennisbankPage"));
const HandleidingPagina = React.lazy(() => import("./pages/HandleidingPagina"));
const BeheerInstellingenPage = React.lazy(
  () => import("./pages/BeheerInstellingenPage"),
);
const PrijsbeheerPage = React.lazy(() => import("./pages/PrijsbeheerPage"));
const ManagerDashboardPage = React.lazy(
  () => import("./pages/ManagerDashboardPage"),
);
const FinancieelDashboardPagina = React.lazy(
  () => import("./pages/FinancieelDashboardPagina"),
);
const DatakwaliteitPagina = React.lazy(
  () => import("./pages/DatakwaliteitPagina"),
);
const BestandscontrolePagina = React.lazy(
  () => import("./pages/BestandscontrolePagina"),
);
const ActiviteitPagina = React.lazy(() => import("./pages/ActiviteitPagina"));
const PlanningAssistentenPagina = React.lazy(
  () => import("./pages/PlanningAssistentenPagina"),
);
const AfgerondeDossiersPage = React.lazy(
  () => import("./pages/AfgerondeDossiersPage"),
);
const AlleAfgerondeDossiersPage = React.lazy(
  () => import("./pages/AlleAfgerondeDossiersPage"),
);
const ModalShowcase = React.lazy(() =>
  import("./components/modal").then((module) => ({
    default: module.ModalShowcase,
  })),
);
const MigrationDashboardPage = React.lazy(
  () => import("./pages/MigrationDashboardPage"),
);
const MigrationWorkflowAPage = React.lazy(() =>
  import("./pages/MigrationWorkflowAPage").then((module) => ({
    default: module.MigrationWorkflowAPage,
  })),
);
const MigrationWorkflowBPage = React.lazy(() =>
  import("./pages/MigrationWorkflowBPage").then((module) => ({
    default: module.MigrationWorkflowBPage,
  })),
);
const MigrationTransfersPage = React.lazy(() =>
  import("./pages/MigrationTransfersPage").then((module) => ({
    default: module.MigrationTransfersPage,
  })),
);
const MigrationBatchPage = React.lazy(() =>
  import("./pages/MigrationBatchPage").then((module) => ({
    default: module.MigrationBatchPage,
  })),
);
const MigrationVmMonitorPage = React.lazy(() =>
  import("./pages/MigrationVmMonitorPage").then((module) => ({
    default: module.MigrationVmMonitorPage,
  })),
);
const MigrationInvoicesPage = React.lazy(() =>
  import("./pages/MigrationInvoicesPage").then((module) => ({
    default: module.MigrationInvoicesPage,
  })),
);
const MigrationInvoiceVmPage = React.lazy(() =>
  import("./pages/MigrationInvoiceVmPage").then((module) => ({
    default: module.MigrationInvoiceVmPage,
  })),
);
const MigrationFinancialComparePage = React.lazy(
  () => import("./pages/MigrationFinancialComparePage"),
);
const WebinarsLayout = React.lazy(() => import("./pages/WebinarsLayout"));
const WebinarsPagina = React.lazy(() => import("./pages/WebinarsPagina"));
const WebinarBetalingenPagina = React.lazy(
  () => import("./pages/WebinarBetalingenPagina"),
);
const WebinarKortingscodesPagina = React.lazy(
  () => import("./pages/WebinarKortingscodesPagina"),
);
const WebinarTekstenPagina = React.lazy(
  () => import("./pages/WebinarTekstenPagina"),
);
const WebinarInstellingenPagina = React.lazy(
  () => import("./pages/WebinarInstellingenPagina"),
);
const WebinarGaiaPagina = React.lazy(() => import("./pages/WebinarGaiaPagina"));
const WebinarMarketingPagina = React.lazy(
  () => import("./pages/WebinarMarketingPagina"),
);
const WebinarEvaluatiesPagina = React.lazy(
  () => import("./pages/WebinarEvaluatiesPagina"),
);

const MIGRATION_WORKBENCH_ROLES = [Role.ADMIN] as const;

// Rollen met toegang tot het persoonlijke dashboard. Een account met
// uitsluitend WEBINAR_MANAGER (Janneke, basisdocument W5) valt hierbuiten en
// landt op /webinars — zonder deze case blijft zo'n account in de unauth-lus.
const DASHBOARD_ROLLEN = [
  Role.ARTS,
  Role.ASSISTENT,
  Role.RADIOLOGIE_ASSISTENT,
  Role.WAARNEMEND_ASSISTENT,
  Role.VOORBEREIDINGS_ASSISTENT,
  Role.PM,
  Role.INPLANNER,
  Role.MANAGER,
  Role.FINANCIELE_MANAGER,
  Role.ADMIN,
] as const;

const withPageSuspense = (element: React.ReactElement) => (
  <Suspense fallback={<PaginaLader />}>{element}</Suspense>
);

// Niet ingelogd: onthoud het doel (bijv. /m/<code> uit een WhatsApp-melding)
// en ga naar /login. Na het inloggen stuurt NaLoginDoel er alsnog heen.
const NaarLogin: React.FC = () => {
  const locatie = useLocation();
  bewaarDoel(locatie.pathname + locatie.search);
  return <Navigate to="/login" replace />;
};

const NaLoginDoel: React.FC = () => {
  const navigate = useNavigate();
  useEffect(() => {
    const doel = neemDoel();
    if (doel) navigate(doel, { replace: true });
  }, [navigate]);
  return null;
};

const App: React.FC = () => {
  const { user, loading, hasRole } = useAuth();
  // In het venster van de pdf-editor geen menu, zoeken of sneltoetsen: die
  // liggen over de editor, en Cmd+D zou zonder vraag weg navigeren.
  const inPdfVenster = /^\/dossier\/[^/]+\/pdf\//.test(useLocation().pathname);

  // Toon laad-indicator terwijl de authenticatie wordt gecontroleerd
  if (loading) {
    return <PaginaLader variant="fullscreen" />;
  }

  return (
    <>
      {user ? (
        <>
          {/* Menu alleen voor ingelogde gebruikers: op de inlogpagina hoort
              geen hamburger-knop te staan. */}
          {!inPdfVenster && <NavMenu />}
          {/*<p>*/}
          {/*  Logged in as <b>{user.email}</b>*/}
          {/*  <button onClick={logout} style={{ marginLeft: 12 }}>*/}
          {/*    Logout*/}
          {/*  </button>*/}
          {/*</p>*/}

          <Routes>
            <Route
              path="/"
              element={
                hasRole(Role.PM, Role.MANAGER, Role.ADMIN) ? (
                  <Navigate to="/manager-dashboard" replace />
                ) : hasRole(Role.WEBINAR_MANAGER) &&
                  !hasRole(...DASHBOARD_ROLLEN) ? (
                  <Navigate to="/webinars" replace />
                ) : (
                  <Navigate to="/dashboard" replace />
                )
              }
            />
            <Route
              path="/admin"
              element={
                <RoleGuard
                  roles={[Role.ADMIN]}
                  fallback={<Navigate to="/unauth" />}
                >
                  {withPageSuspense(<AdminPage />)}
                </RoleGuard>
              }
            />
            <Route
              path="/prijsbeheer"
              element={
                <RoleGuard
                  roles={[Role.ADMIN]}
                  fallback={<Navigate to="/unauth" replace />}
                >
                  {withPageSuspense(<PrijsbeheerPage />)}
                </RoleGuard>
              }
            />
            <Route
              path="/dossiers/new"
              element={
                <RoleGuard
                  roles={[
                    Role.ADMIN,
                    Role.PM,
                    Role.MANAGER,
                    Role.FINANCIELE_MANAGER,
                    Role.INPLANNER,
                  ]}
                  fallback={<Navigate to="/unauth" />}
                >
                  {withPageSuspense(<DossierCreatePage />)}
                </RoleGuard>
              }
            />
            <Route
              path="/dossier/:kenmerk"
              element={
                <RoleGuard
                  roles={[
                    Role.ARTS,
                    Role.ASSISTENT,
                    Role.RADIOLOGIE_ASSISTENT,
                    Role.WAARNEMEND_ASSISTENT,
                    Role.VOORBEREIDINGS_ASSISTENT,
                    Role.PM,
                    Role.INPLANNER,
                    Role.MANAGER,
                    Role.FINANCIELE_MANAGER,
                    Role.ADMIN,
                  ]}
                  fallback={<Navigate to="/unauth" replace />}
                >
                  {withPageSuspense(<DossierPagina />)}
                </RoleGuard>
              }
            />
            <Route
              path="/dossier/:kenmerk/pdf/:docId"
              element={
                <RoleGuard
                  roles={[
                    Role.ARTS,
                    Role.ASSISTENT,
                    Role.RADIOLOGIE_ASSISTENT,
                    Role.WAARNEMEND_ASSISTENT,
                    Role.VOORBEREIDINGS_ASSISTENT,
                    Role.PM,
                    Role.INPLANNER,
                    Role.MANAGER,
                    Role.FINANCIELE_MANAGER,
                    Role.ADMIN,
                  ]}
                  fallback={<Navigate to="/unauth" replace />}
                >
                  {withPageSuspense(<PdfBewerkenPagina />)}
                </RoleGuard>
              }
            />
            <Route
              path="/manager-dashboard"
              element={
                <RoleGuard
                  roles={[
                    Role.PM,
                    Role.MANAGER,
                    Role.FINANCIELE_MANAGER,
                    Role.ADMIN,
                  ]}
                  fallback={<Navigate to="/unauth" replace />}
                >
                  {withPageSuspense(<ManagerDashboardPage />)}
                </RoleGuard>
              }
            />
            <Route
              path="/financieel-dashboard"
              element={
                // Besluit #1 basisdocument financieel dashboard: alléén
                // MANAGER — bewust géén ADMIN of FINANCIELE_MANAGER.
                <RoleGuard
                  roles={[Role.MANAGER]}
                  fallback={<Navigate to="/unauth" replace />}
                >
                  {withPageSuspense(<FinancieelDashboardPagina />)}
                </RoleGuard>
              }
            />
            <Route
              path="/datakwaliteit"
              element={
                // Datakwaliteit-workbench (ontwerpdoc 2026-07-24); sinds D28
                // (overleg Martine 02-09-2026) alléén ADMIN, ook in de backend.
                <RoleGuard
                  roles={[Role.ADMIN]}
                  fallback={<Navigate to="/unauth" replace />}
                >
                  {withPageSuspense(<DatakwaliteitPagina />)}
                </RoleGuard>
              }
            />
            <Route
              path="/bestandscontrole"
              element={
                // I17 (besluit Niels 07-10-2026): alleen MANAGER en ADMIN,
                // ook in de backend (BestandscontroleGuard).
                <RoleGuard
                  roles={[Role.MANAGER, Role.ADMIN]}
                  fallback={<Navigate to="/unauth" replace />}
                >
                  {withPageSuspense(<BestandscontrolePagina />)}
                </RoleGuard>
              }
            />
            <Route
              path="/activiteit"
              element={
                // WP6 (besluit Niels 12-10-2026): activiteitenlog en
                // onboarding, alleen MANAGER en ADMIN, ook in de backend
                // (ActiviteitGuard).
                <RoleGuard
                  roles={[Role.MANAGER, Role.ADMIN]}
                  fallback={<Navigate to="/unauth" replace />}
                >
                  {withPageSuspense(<ActiviteitPagina />)}
                </RoleGuard>
              }
            />
            <Route
              path="/planning-assistenten"
              element={
                // Feedback kantoor 1-11-2026: knop naar het planningsbestand
                // van de assistenten; de rechten op het bestand regelt
                // SharePoint. Lezen van de koppeling heeft geen rolcheck.
                <RoleGuard
                  roles={[
                    Role.ASSISTENT,
                    Role.RADIOLOGIE_ASSISTENT,
                    Role.WAARNEMEND_ASSISTENT,
                    Role.VOORBEREIDINGS_ASSISTENT,
                    Role.PM,
                    Role.MANAGER,
                    Role.ADMIN,
                  ]}
                  fallback={<Navigate to="/unauth" replace />}
                >
                  {withPageSuspense(<PlanningAssistentenPagina />)}
                </RoleGuard>
              }
            />
            <Route
              path="/dashboard"
              element={
                <RoleGuard
                  roles={[
                    Role.ARTS,
                    Role.ASSISTENT,
                    Role.RADIOLOGIE_ASSISTENT,
                    Role.WAARNEMEND_ASSISTENT,
                    Role.VOORBEREIDINGS_ASSISTENT,
                    Role.PM,
                    Role.INPLANNER,
                    Role.MANAGER,
                    Role.FINANCIELE_MANAGER,
                    Role.ADMIN,
                  ]}
                  fallback={<Navigate to="/unauth" replace />}
                >
                  {withPageSuspense(<DashboardPage />)}
                </RoleGuard>
              }
            />
            {/* Knop in een WhatsApp-melding; de backend controleert toegang. */}
            <Route
              path="/m/:code"
              element={withPageSuspense(<MeldingLinkPagina />)}
            />
            {/* Link in de overzichtsmail (dossier-id); idem. */}
            <Route
              path="/d/:id"
              element={withPageSuspense(<MeldingLinkPagina />)}
            />
            <Route
              path="/mijn-instellingen"
              element={
                <RoleGuard
                  roles={[
                    Role.ARTS,
                    Role.ASSISTENT,
                    Role.RADIOLOGIE_ASSISTENT,
                    Role.WAARNEMEND_ASSISTENT,
                    Role.VOORBEREIDINGS_ASSISTENT,
                    Role.PM,
                    Role.INPLANNER,
                    Role.MANAGER,
                    Role.FINANCIELE_MANAGER,
                    Role.ADMIN,
                  ]}
                  fallback={<Navigate to="/unauth" replace />}
                >
                  {withPageSuspense(<MijnInstellingenPagina />)}
                </RoleGuard>
              }
            />
            <Route
              path="/afgeronde-dossiers"
              element={
                <RoleGuard
                  roles={[
                    Role.ARTS,
                    Role.ASSISTENT,
                    Role.RADIOLOGIE_ASSISTENT,
                    Role.WAARNEMEND_ASSISTENT,
                    Role.VOORBEREIDINGS_ASSISTENT,
                    Role.PM,
                    Role.INPLANNER,
                    Role.MANAGER,
                    Role.FINANCIELE_MANAGER,
                    Role.ADMIN,
                  ]}
                  fallback={<Navigate to="/unauth" replace />}
                >
                  {withPageSuspense(<AfgerondeDossiersPage />)}
                </RoleGuard>
              }
            />
            <Route
              path="/alle-afgeronde-dossiers"
              element={
                <RoleGuard
                  roles={[Role.PM, Role.MANAGER, Role.ADMIN]}
                  fallback={<Navigate to="/unauth" replace />}
                >
                  {withPageSuspense(<AlleAfgerondeDossiersPage />)}
                </RoleGuard>
              }
            />
            <Route
              path="/medewerker"
              element={
                <RoleGuard
                  roles={[Role.ADMIN, Role.MANAGER]}
                  fallback={<Navigate to="/unauth" replace />}
                >
                  {withPageSuspense(<MedewerkerPage />)}
                </RoleGuard>
              }
            />
            <Route
              path="/product"
              element={
                <RoleGuard
                  roles={[Role.ADMIN, Role.MANAGER, Role.FINANCIELE_MANAGER]}
                  fallback={<Navigate to="/unauth" replace />}
                >
                  {withPageSuspense(<ProductPage />)}
                </RoleGuard>
              }
            />
            <Route
              path="/financien"
              element={
                // Q18 (fixronde oktober 2026): Mijn financiën is nog niet af;
                // buiten DEV stil terug naar het dashboard, zonder melding.
                isDevOmgeving() ? (
                  <RoleGuard
                    roles={[
                      Role.ARTS,
                      Role.ASSISTENT,
                      Role.RADIOLOGIE_ASSISTENT,
                      Role.WAARNEMEND_ASSISTENT,
                      Role.VOORBEREIDINGS_ASSISTENT,
                      Role.PM,
                      Role.INPLANNER,
                      Role.MANAGER,
                      Role.FINANCIELE_MANAGER,
                      Role.ADMIN,
                    ]}
                    fallback={<Navigate to="/unauth" replace />}
                  >
                    {withPageSuspense(<FinancienPage />)}
                  </RoleGuard>
                ) : (
                  <Navigate to="/dashboard" replace />
                )
              }
            />
            <Route
              path="/waarnemers"
              element={
                <RoleGuard
                  roles={[Role.MANAGER, Role.ADMIN]}
                  fallback={<Navigate to="/unauth" replace />}
                >
                  {withPageSuspense(<WaarnemersPage />)}
                </RoleGuard>
              }
            />
            <Route
              path="/facturen"
              element={
                <RoleGuard
                  roles={[Role.MANAGER, Role.ADMIN, Role.FINANCIELE_MANAGER]}
                  fallback={<Navigate to="/unauth" replace />}
                >
                  {withPageSuspense(<FacturenPage />)}
                </RoleGuard>
              }
            />

            <Route
              path="/aanvullende-facturen"
              element={
                <RoleGuard
                  roles={[Role.MANAGER, Role.ADMIN]}
                  fallback={<Navigate to="/unauth" replace />}
                >
                  {withPageSuspense(<AanvullendeFacturenPage />)}
                </RoleGuard>
              }
            />

            <Route
              path="/inplannen"
              element={
                // K4: Inplannen alleen voor Inplanner, Admin en PM (Manager eraf).
                <RoleGuard
                  roles={[Role.ADMIN, Role.INPLANNER, Role.PM]}
                  fallback={<Navigate to="/unauth" />}
                >
                  {withPageSuspense(<PlanPage />)}
                </RoleGuard>
              }
            />

            <Route
              path="/voorbereidingen"
              element={
                // A14: zelfde rollen als het menu-item en de backend.
                <RoleGuard
                  roles={[
                    Role.PM,
                    Role.MANAGER,
                    Role.ADMIN,
                    Role.VOORBEREIDINGS_ASSISTENT,
                  ]}
                  fallback={<Navigate to="/unauth" replace />}
                >
                  {withPageSuspense(<VoorbereidingenPagina />)}
                </RoleGuard>
              }
            />

            <Route
              path="/salaris"
              element={
                <RoleGuard
                  roles={[Role.ADMIN, Role.MANAGER, Role.FINANCIELE_MANAGER]}
                  fallback={<Navigate to="/unauth" />}
                >
                  {withPageSuspense(<PayrollPage />)}
                </RoleGuard>
              }
            />

            {/* /locatie-overzicht: route verwijderd in D30 (overleg Martine
                02-09-2026); LocatieOverzichtPage blijft in de codebase. */}

            <Route
              path="/modal-showcase"
              element={
                <RoleGuard
                  roles={[Role.ADMIN]}
                  fallback={<Navigate to="/unauth" />}
                >
                  {withPageSuspense(<ModalShowcase />)}
                </RoleGuard>
              }
            />
            <Route
              path="/migration"
              element={
                <RoleGuard
                  roles={[...MIGRATION_WORKBENCH_ROLES]}
                  fallback={<Navigate to="/unauth" replace />}
                >
                  {withPageSuspense(<MigrationDashboardPage />)}
                </RoleGuard>
              }
            />
            <Route
              path="/migration/workflow-a"
              element={
                <RoleGuard
                  roles={[...MIGRATION_WORKBENCH_ROLES]}
                  fallback={<Navigate to="/unauth" replace />}
                >
                  {withPageSuspense(<MigrationWorkflowAPage />)}
                </RoleGuard>
              }
            />
            <Route
              path="/migration/workflow-b"
              element={
                <RoleGuard
                  roles={[...MIGRATION_WORKBENCH_ROLES]}
                  fallback={<Navigate to="/unauth" replace />}
                >
                  {withPageSuspense(<MigrationWorkflowBPage />)}
                </RoleGuard>
              }
            />
            <Route
              path="/migration/transfers"
              element={
                <RoleGuard
                  roles={[...MIGRATION_WORKBENCH_ROLES]}
                  fallback={<Navigate to="/unauth" replace />}
                >
                  {withPageSuspense(<MigrationTransfersPage />)}
                </RoleGuard>
              }
            />
            <Route
              path="/migration/batch"
              element={
                <RoleGuard
                  roles={[...MIGRATION_WORKBENCH_ROLES]}
                  fallback={<Navigate to="/unauth" replace />}
                >
                  {withPageSuspense(<MigrationBatchPage />)}
                </RoleGuard>
              }
            />
            <Route
              path="/migration/vm"
              element={
                <RoleGuard
                  roles={[...MIGRATION_WORKBENCH_ROLES]}
                  fallback={<Navigate to="/unauth" replace />}
                >
                  {withPageSuspense(<MigrationVmMonitorPage />)}
                </RoleGuard>
              }
            />
            <Route
              path="/migration/invoices"
              element={
                <RoleGuard
                  roles={[...MIGRATION_WORKBENCH_ROLES]}
                  fallback={<Navigate to="/unauth" replace />}
                >
                  {withPageSuspense(<MigrationInvoicesPage />)}
                </RoleGuard>
              }
            />
            <Route
              path="/migration/invoices/vm"
              element={
                <RoleGuard
                  roles={[...MIGRATION_WORKBENCH_ROLES]}
                  fallback={<Navigate to="/unauth" replace />}
                >
                  {withPageSuspense(<MigrationInvoiceVmPage />)}
                </RoleGuard>
              }
            />
            <Route
              path="/migration/financial-compare"
              element={
                <RoleGuard
                  roles={[...MIGRATION_WORKBENCH_ROLES]}
                  fallback={<Navigate to="/unauth" replace />}
                >
                  {withPageSuspense(<MigrationFinancialComparePage />)}
                </RoleGuard>
              }
            />

            <Route
              path="/kennisbank"
              element={
                <RoleGuard
                  roles={[
                    Role.ARTS,
                    Role.ASSISTENT,
                    Role.RADIOLOGIE_ASSISTENT,
                    Role.WAARNEMEND_ASSISTENT,
                    Role.VOORBEREIDINGS_ASSISTENT,
                    Role.PM,
                    Role.INPLANNER,
                    Role.MANAGER,
                    Role.FINANCIELE_MANAGER,
                    Role.ADMIN,
                  ]}
                  fallback={<Navigate to="/unauth" />}
                >
                  {withPageSuspense(<KennisbankPage />)}
                </RoleGuard>
              }
            />

            {/* Handleiding: derde tab van de Kennisbank, zelfde rollen. */}
            <Route
              path="/kennisbank/handleiding/:slug?"
              element={
                <RoleGuard
                  roles={[
                    Role.ARTS,
                    Role.ASSISTENT,
                    Role.RADIOLOGIE_ASSISTENT,
                    Role.WAARNEMEND_ASSISTENT,
                    Role.VOORBEREIDINGS_ASSISTENT,
                    Role.PM,
                    Role.INPLANNER,
                    Role.MANAGER,
                    Role.FINANCIELE_MANAGER,
                    Role.ADMIN,
                  ]}
                  fallback={<Navigate to="/unauth" />}
                >
                  {withPageSuspense(<HandleidingPagina />)}
                </RoleGuard>
              }
            />

            <Route
              path="/beheer/standaardwaarden"
              element={
                <RoleGuard
                  roles={[Role.MANAGER, Role.ADMIN]}
                  fallback={<Navigate to="/unauth" replace />}
                >
                  {withPageSuspense(<BeheerInstellingenPage />)}
                </RoleGuard>
              }
            />

            <Route
              path="/webinars"
              element={
                // Besluit W5 webinars-basisdocument, aangescherpt in D29
                // (overleg Martine 02-09-2026): ADMIN ∪ WEBINAR_MANAGER (drie
                // lagen: NavMenu, deze RoleGuard, backend-guard). De eindfase
                // hangt er tabs onder (§9); de guard dekt ze allemaal.
                <RoleGuard
                  roles={[Role.ADMIN, Role.WEBINAR_MANAGER]}
                  fallback={<Navigate to="/unauth" replace />}
                >
                  {withPageSuspense(<WebinarsLayout />)}
                </RoleGuard>
              }
            >
              <Route index element={withPageSuspense(<WebinarsPagina />)} />
              <Route
                path="betalingen"
                element={withPageSuspense(<WebinarBetalingenPagina />)}
              />
              <Route
                path="kortingscodes"
                element={withPageSuspense(<WebinarKortingscodesPagina />)}
              />
              <Route
                path="teksten"
                element={withPageSuspense(<WebinarTekstenPagina />)}
              />
              <Route
                path="instellingen"
                element={withPageSuspense(<WebinarInstellingenPagina />)}
              />
              <Route
                path="marketing"
                element={withPageSuspense(<WebinarMarketingPagina />)}
              />
              <Route
                path="gaia"
                element={withPageSuspense(<WebinarGaiaPagina />)}
              />
              <Route
                path="evaluaties"
                element={withPageSuspense(<WebinarEvaluatiesPagina />)}
              />
            </Route>

            <Route
              path="/unauth"
              element={
                hasRole(Role.PM, Role.MANAGER, Role.ADMIN) ? (
                  <Navigate to="/manager-dashboard" replace />
                ) : hasRole(...DASHBOARD_ROLLEN) ? (
                  <Navigate to="/dashboard" replace />
                ) : hasRole(Role.WEBINAR_MANAGER) ? (
                  <Navigate to="/webinars" replace />
                ) : (
                  <p>Sorry, geen toegang.</p>
                )
              }
            />
            {/* Default fallback */}
            <Route
              path="*"
              element={
                hasRole(Role.PM, Role.MANAGER, Role.ADMIN) ? (
                  <Navigate to="/manager-dashboard" replace />
                ) : hasRole(Role.WEBINAR_MANAGER) &&
                  !hasRole(...DASHBOARD_ROLLEN) ? (
                  <Navigate to="/webinars" replace />
                ) : (
                  <Navigate to="/dashboard" replace />
                )
              }
            />
          </Routes>
          {!inPdfVenster && (
            <>
              <NotificationTray />
              <DossierQuickSearch />
              <UploadProgressFab />
              <GlobalShortcuts />
            </>
          )}
          <NaLoginDoel />
        </>
      ) : (
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="*" element={<NaarLogin />} />
        </Routes>
      )}
    </>
  );
};

export default App;
